/**
 * The DeepFace face-embedding step (Flask service) is best-effort on top of
 * the profile photo upload (Laravel service, always required). If Flask is
 * down, slow, or simply rejects the image, the photo itself must still be
 * saved — these helpers turn that failure into a message instead of letting
 * it fail the whole upload.
 */

/**
 * True when the request never reached the Flask service at all (connection
 * refused, DNS failure, CORS, timeout) — as opposed to Flask responding with
 * a real error (e.g. "no face detected").
 *
 * The api.js response interceptor rejects with `error.response.data` (a
 * plain `{message, error}` object) once a response comes back, but lets a
 * network-level failure through untouched, so it still carries Axios's own
 * error shape (`isAxiosError`, `code`, no `response`).
 */
export const isFaceServiceUnreachable = (err) => (
  Boolean(err?.isAxiosError) && !err?.response
);

/**
 * One sentence to append after "profile photo saved" when the embedding
 * step failed, distinguishing "service unavailable, try again later" from
 * "the photo itself was rejected (e.g. no face found)".
 */
export const describeFaceEmbeddingFailure = (err) => {
  if (isFaceServiceUnreachable(err)) {
    return "Le service de reconnaissance faciale est momentanément indisponible. Réessayez depuis cette page plus tard pour activer la recherche de vos photos.";
  }

  const detail = err?.error && err.error !== err.message ? ` (${err.error})` : '';
  const reason = err?.message ? `${err.message}${detail}` : "le scan du visage a échoué";

  return `${reason} Réessayez avec une photo plus nette de votre visage.`;
};
